import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useApp } from '../context/AppContext';
import { createFuelFill, getFuelFills, getNotificationSettings } from '../services/api';
import { toLocalISODate } from '../utils/dateUtils';

export default function FuelFill() {
  const { vehicles } = useApp();
  const [form, setForm] = useState({
    vehicle: '',
    fillDate: toLocalISODate(),
    meterAtFill: '',
    totalCostRs: '',
    pricePerLiter: '',
  });
  const [recentFills, setRecentFills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const vehicleList = useMemo(
    () => [...vehicles].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [vehicles]
  );

  const selectedVehicle = vehicleList.find((v) => String(v._id) === String(form.vehicle));

  const derivedLiters = useMemo(() => {
    const total = Number(form.totalCostRs);
    const price = Number(form.pricePerLiter);
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(price) || price <= 0) {
      return null;
    }
    return Math.round((total / price) * 100) / 100;
  }, [form.totalCostRs, form.pricePerLiter]);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFuelFills({ limit: 10 });
      setRecentFills(Array.isArray(res?.fills) ? res.fills : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load recent fills');
      setRecentFills([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  useEffect(() => {
    getNotificationSettings()
      .then((settings) => {
        if (form.pricePerLiter !== '') return;
        const fuelType = selectedVehicle?.fuelType === 'petrol' ? 'petrol' : 'diesel';
        const fallback = fuelType === 'petrol'
          ? settings?.defaultPetrolPricePerLiter
          : settings?.defaultDieselPricePerLiter;
        if (fallback != null) {
          setForm((f) => ({ ...f, pricePerLiter: String(fallback) }));
        }
      })
      .catch(() => {});
  }, [selectedVehicle, form.pricePerLiter]);

  useEffect(() => {
    if (!selectedVehicle || form.meterAtFill !== '') return;
    if (Number.isFinite(selectedVehicle.currentMileage)) {
      setForm((f) => ({ ...f, meterAtFill: String(selectedVehicle.currentMileage) }));
    }
  }, [selectedVehicle, form.meterAtFill]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle) return toast.error('Select a vehicle');
    if (!form.meterAtFill) return toast.error('Enter meter reading (km)');
    if (!form.totalCostRs) return toast.error('Enter total amount spent (Rs)');
    if (!form.pricePerLiter) return toast.error('Enter price per liter (Rs)');

    setSubmitting(true);
    try {
      await createFuelFill({
        vehicle: form.vehicle,
        fillDate: form.fillDate,
        meterAtFill: Number(form.meterAtFill),
        totalCostRs: Number(form.totalCostRs),
        pricePerLiter: Number(form.pricePerLiter),
      });
      toast.success(
        derivedLiters
          ? `Fill saved — about ${derivedLiters} L recorded`
          : 'Fuel fill saved'
      );
      setForm((f) => ({
        ...f,
        totalCostRs: '',
        meterAtFill: selectedVehicle?.currentMileage != null
          ? String(selectedVehicle.currentMileage)
          : '',
      }));
      await loadRecent();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save fuel fill');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFillDate = (value) => {
    if (!value) return '—';
    try {
      return format(new Date(value), 'dd MMM yyyy');
    } catch (_err) {
      return '—';
    }
  };

  return (
    <>
      <div className="page-header">
        <h2>Fuel Fill</h2>
        <p>
          Log when a vehicle is filled at the pump. Enter the <strong>total Rs spent</strong> and
          price per liter — the system calculates liters and tracks when fuel is running low (~10 km left).
        </p>
      </div>

      <div className="card" style={{ maxWidth: 720, marginBottom: 24 }}>
        <form onSubmit={handleSubmit}>
          <div className="form-grid cols2" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label>Vehicle</label>
              <select
                value={form.vehicle}
                onChange={(e) => setField('vehicle', e.target.value)}
                required
              >
                <option value="">— Select vehicle —</option>
                {vehicleList.map((v) => (
                  <option key={v._id} value={v._id}>
                    {v.name} ({v.plate})
                    {v.fuelAverage ? ` — ${v.fuelAverage} km/L` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Fill date</label>
              <input
                type="date"
                value={form.fillDate}
                onChange={(e) => setField('fillDate', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-grid cols3" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label>Meter at fill (km)</label>
              <input
                type="number"
                min="0"
                placeholder="Odometer reading"
                value={form.meterAtFill}
                onChange={(e) => setField('meterAtFill', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Total spent (Rs)</label>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 5000"
                value={form.totalCostRs}
                onChange={(e) => setField('totalCostRs', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Price per liter (Rs)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 285"
                value={form.pricePerLiter}
                onChange={(e) => setField('pricePerLiter', e.target.value)}
                required
              />
            </div>
          </div>

          {derivedLiters != null && (
            <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
              ≈ <strong>{derivedLiters} liters</strong> filled
              {selectedVehicle?.fuelAverage
                ? ` — about ${Math.round(derivedLiters * selectedVehicle.fuelAverage)} km range`
                : ''}
            </p>
          )}

          {!selectedVehicle?.fuelAverage && form.vehicle && (
            <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
              Set fuel average (km/L) for this vehicle in Manage so low-fuel alerts work.
            </p>
          )}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save fuel fill'}
          </button>
        </form>
      </div>

      <div className="card" style={{ maxWidth: 900 }}>
        <p className="card-title" style={{ fontWeight: 600, marginBottom: 12 }}>Recent fills</p>
        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : recentFills.length === 0 ? (
          <p className="text-muted">No fills logged yet.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vehicle</th>
                  <th>Meter</th>
                  <th>Rs spent</th>
                  <th>Liters</th>
                </tr>
              </thead>
              <tbody>
                {recentFills.map((f) => (
                  <tr key={f._id}>
                    <td>{formatFillDate(f.fillDate)}</td>
                    <td>{f.vehicle?.name || '—'} <span className="mono text-muted">{f.vehicle?.plate}</span></td>
                    <td className="mono">{f.meterAtFill}</td>
                    <td>{f.totalCostRs != null ? `Rs ${f.totalCostRs}` : '—'}</td>
                    <td>{f.litersFilled}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
